import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { ATTRAPPE, hole, sende } from "./klient";
import * as AF from "./attrappe/faelle";

/* ══════════════════════════════════════════════════════════════════
   Die Fälle — was einen Menschen braucht (24.09.2026, 51-meldungen.md).

   Eine Liste aus dem Katalog der API (`GET /faelle`) für alle Stellen, die
   bisher eigene Abfragen hatten. Welche Spalte der Startseite einen Fall
   zeigt, entscheidet die Oberfläche aus Form, Gruppe und Besitzer:

   | Spalte | nimmt |
   |---|---|
   | Meine Aufgaben | Aufgaben, die mir gehören, und freie meiner Gruppen außer „Hof & Technik" |
   | Zu verteilen | für die Disposition: die Aufgaben von „Hof & Technik" und die freien, die vergeben werden müssen |
   | Zu entscheiden | Entscheidungen |

   Die Wörter kommen aus de.json (`fall.*`) — die API liefert Werte, keine
   Sätze (CLAUDE.md).
   ══════════════════════════════════════════════════════════════════ */

export type Stufe = "demnaechst" | "heute" | "ueberfaellig" | "blockiert";
export type Form = "aufgabe" | "entscheidung" | "hinweis";
export type Gruppe = "disposition" | "technik" | "vertrieb" | "buchhaltung" | "verwaltung";

export type Fall = {
  schluessel: string;
  art: string;
  form: Form;
  stufe: Stufe;
  gruppe: Gruppe;
  eskalation: Gruppe | null;
  besitzer: { id: string; name: string } | null;
  vorgang: string | null;
  kunde: string | null;
  kennung: string | null;
  belegung_id: string | null;
  faellig: string | null;
  seit: string | null;
  daten: Record<string, string>;
  weg: string | null;
};

export type Faelle = { faelle: Fall[]; gruppen: Gruppe[]; ich: string };

export const STUFENRANG: Record<Stufe, number> = { demnaechst: 0, heute: 1, ueberfaellig: 2, blockiert: 3 };

export const faelleSchluessel = ["faelle"] as const;

export async function faelleHolen(vorgang?: string): Promise<Faelle> {
  if (ATTRAPPE) return AF.FAELLE;
  return hole<Faelle>(vorgang ? `/faelle?vorgang=${encodeURIComponent(vorgang)}` : "/faelle");
}

/**
 * Die Fälle — und jede Minute neu, solange die Seite offen ist
 * (Entscheidung K: „Die Liste lädt jede Minute nach").
 */
export const useFaelle = (vorgang?: string) =>
  useQuery({
    queryKey: [...faelleSchluessel, vorgang ?? ""],
    queryFn: () => faelleHolen(vorgang),
    refetchInterval: 60_000,
  });

/* ══════════════ Verteilen ══════════════ */

export type Wer = { id: string; name: string } | null;
export type Verteilzeile = {
  belegung_id: string;
  kennung: string;
  typ: string | null;
  vorgang: string | null;
  kunde: string | null;
  beginn: string;
  ende: string;
  tag_beginn: string;
  tag_ende: string;
  /** Der Tag, an dem vorbereitet wird — der Arbeitstag vor der Übergabe nach
   *  der Regel der Maschine (25.09.2026). Fehlt er, rechnet `arbeitstagVorher`. */
  tag_vorbereitung?: string;
  uebergabeart: "liefern" | "abholen";
  /** Der Einsatzort (25.09.2026): Name, Ort, Entfernung vom Hof in km. */
  ort: string | null;
  ortschaft: string | null;
  entfernung: number | null;
  /** Was das Gerät von dem verlangt, der es bedient — Codes der Qualifikationen. */
  verlangt: string[];
  bediener: { id: string; name: string }[];
  ausgegeben: boolean;
  zurueck: boolean;
  vorbereitung: { erledigt: boolean; wer: Wer; zugeteilt_von: string | null };
  uebergabe: { wer: Wer; zugeteilt_von: string | null };
  ruecknahme: { wer: Wer; zugeteilt_von: string | null };
};
/** Ein Mensch, dem die Disposition etwas zuteilen kann (`GET /verteilen`, `leute`). */
export type Verteilperson = {
  id: string;
  name: string;
  kuerzel: string | null;
  rollen: string[];
  /** Hof & Technik — die erste Wahl. */
  stamm: boolean;
  qualifikationen: { code: string; gueltig_bis: string | null }[];
  /** Steht als Mensch im Belegungsband — nur sie haben in Einteilen eine Zeile (docs/65, E5). Fehlt es (Attrappe), gilt ja. */
  disponierbar?: boolean;
};
/** Ein Eintrag eines Menschen im Belegungsband: Urlaub, Einsatz als Fahrer, Termin. */
export type Bandeintrag = {
  person_id: string;
  art: string;
  von: string;
  bis: string;
  kennung: string | null;
  vorgang: string | null;
};
export type Verteilstand = {
  von: string;
  bis: string;
  zeilen: Verteilzeile[];
  leute: Verteilperson[];
  band: Bandeintrag[];
  qualifikationen: { code: string; label: string }[];
};
export type Verteilart = "vorbereitung" | "uebergabe" | "ruecknahme";
export type Zuteilung = { belegung_id: string; art: Verteilart; wer: string | null };

export const useVerteilen = (von: string, bis: string, an = true) =>
  useQuery({
    queryKey: ["verteilen", von, bis],
    enabled: an,
    /* Beim Blättern stehen die alten Marken, bis die neuen da sind — sonst
       werden die Personalzeilen kurz niedriger und Legende und Fuß springen
       (Luca, 25.09.2026). */
    placeholderData: keepPreviousData,
    queryFn: () => ATTRAPPE
      ? Promise.resolve<Verteilstand>(AF.VERTEILSTAND(von, bis))
      : hole<Verteilstand>(`/verteilen?von=${von}&bis=${bis}`),
  });

export function useZuteilen() {
  const abfragen = useQueryClient();
  return useMutation({
    /* `bestaetigt`: Der Hinweis auf eine fehlende oder abgelaufene
       Qualifikation ist gelesen, zugeteilt wird trotzdem (D-47, E3). */
    mutationFn: (eingabe: Zuteilung[] | { zuteilungen: Zuteilung[]; bestaetigt: boolean }) => {
      const zuteilungen = Array.isArray(eingabe) ? eingabe : eingabe.zuteilungen;
      const bestaetigt = !Array.isArray(eingabe) && eingabe.bestaetigt;
      return ATTRAPPE
        ? Promise.resolve({ zugeteilt: AF.ZUTEILEN(zuteilungen) })
        : sende<{ zugeteilt: number }>("/aufgaben/zuteilen", {
          zuteilungen, ...(bestaetigt ? { qualifikation_bestaetigt: true } : {}),
        });
    },
    onSuccess: () => {
      void abfragen.invalidateQueries({ queryKey: ["verteilen"] });
      void abfragen.invalidateQueries({ queryKey: ["einteilen"] });
      void abfragen.invalidateQueries({ queryKey: faelleSchluessel });
      void abfragen.invalidateQueries({ queryKey: ["start"] });
    },
  });
}
